import * as React from "react";
import { Box, Grid, Link, Typography } from "@mui/material";
import { AppBody } from "../AppBody";
import Breadcrumbs from "../../containers/Breadcrumbs";
import { appHeaderTitle } from "../../common/services/Utilities";
import HelpButton from "../HelpContent/HelpButton";
import makeStyles from "@mui/styles/makeStyles";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import BackButton from "../../containers/BackButton";
import SelectFilterField from "../SelectFilterField";
import { Query } from "../../interfaces";
import PeerGroups from "../../containers/PeerGroups";
import { standardComparisonOptions } from "./PeerGroupUtilities";
import PeerGroupStepTwo from "../../containers/PeerGroupStepTwo";
import EditColumn from "../../containers/EditColumn";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const useStyles = makeStyles({
  stepOne: {
    "& > h2": {
      marginTop: "2rem",
      paddingBottom: ".5rem",
    },
    "& > .select-label": {
      fontWeight: 800,
      paddingBottom: ".5rem",
    },
    "& .MuiFormControl-fullWidth": {
      maxWidth: 400,
    },
  },
});

interface Props {
  appState: Query;
  updateComparisonType: (comparisonType: string) => void;
}

export default function StandardComparison({
  appState,
  updateComparisonType,
}: Props) {
  useInitialPageLoad("peerGroup");
  const classes = useStyles();
  const comparisonType = appState.ui.comparisonType;

  const introParagraph = (
    <>
      {appHeaderTitle("standardPeerGroup")}
      <Typography variant="h2">
        Predefined Standard Comparison Reports for FDIC Insured Institutions
      </Typography>
      <Typography variant="body2" color="primary">
        Select standardized peer groupings and create comparison reports.
      </Typography>
      <Typography variant="body2" color="primary" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupStandard"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  let filteredStandardComparisonOptions = standardComparisonOptions.filter(
    (obj: any) => obj.value === "" || obj.groups?.length !== 1,
  );

  const peerGroups = filteredStandardComparisonOptions
    .map(
      (comparison: any) =>
        comparisonType === comparison.value && comparison.groups,
    )
    .filter((value: any) => value)[0];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle="Standard Comparisons" />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        introText={introParagraph}
        backButton={
          <BackButton
            app="FinancialReporting"
            buttonLabel="Back to Financial Reporting"
            variant="dark"
          />
        }
      >
        <Typography component="div" className={classes.stepOne}>
          <Typography variant="h2">Step 1</Typography>
          <Typography variant="body2" color="primary" className="select-label">
            Choose “who” to compare. This defines the columns of your report.
          </Typography>
          <Box>
            <SelectFilterField
              disabled={appState.ui.pgcStep === "step3"}
              currentValue={comparisonType}
              fieldName={"standardComparison"}
              options={filteredStandardComparisonOptions}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateComparisonType(e.target.value)
              }
            />{" "}
            {appState.ui.pgcStep === "step3" && <EditColumn />}
          </Box>
        </Typography>

        {appState.ui.pgcStep === "step1" ? (
          <PeerGroups groups={peerGroups} />
        ) : (
          <PeerGroupStepTwo />
        )}
      </AppBody>
    </>
  );
}
